import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { DataGrid } from "@mui/x-data-grid";
import {
  getFeeSettings,
  getFeeSettingsHistory,
  updateFeeSettings,
  getStoreRevenue,
  getStorePendingFees,
  collectStoreFees,
  collectStoreFeesBulk,
  undoStoreFeesCollection,
  getStoreRevenueHistory,
  getDeliveryRevenue,
  requestDeliveryFees,
  undoDeliveryFeesRequest,
  getSettlements,
  getSettlementDetail,
  markSettlementPaid,
  getCompanyStoreBreakdown,
  getStoreSubscriptions,
  getStoreSubscriptionDetail,
  overrideStoreSubscriptionStatus,
  clearStoreSubscriptionOverride,
  markInvoicePaid,
  renewStoreSubscription,
  setSubscriptionPriceOverride,
} from "../../api/feesSubscriptionsService";
import {
  formatCurrency,
  formatDate,
  SubscriptionStatusLabels,
  SubscriptionStatusColors,
  InvoiceStatusLabels,
  InvoiceStatusColors,
  COUPON_DISCOUNT_TYPE_LABELS,
} from "./constants";

function couponLabel(code, discountType) {
  if (!code) return null;
  const typeLabel = COUPON_DISCOUNT_TYPE_LABELS[discountType];
  return typeLabel ? `${code} (${typeLabel})` : code;
}

// Synthetic row id appended to the pending-fees grid so a totals row can render as the last row -
// each total then lines up directly under its own column, instead of a separate stat bar off to
// one side.
const TOTALS_ROW_ID = "__totals__";

const TOP_TABS = [
  { value: "rates", label: "Rates" },
  { value: "revenue", label: "Store Revenue" },
  { value: "subscriptions", label: "Store Subscriptions" },
  // Hidden for now: { value: "deliveryRevenue", label: "Delivery Companies" },
];

export default function FeesSubscriptions() {
  const [tab, setTab] = useState("revenue");

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3 }}>
        Fees & Subscriptions
      </Typography>

      <Tabs value={tab} onChange={(e, v) => setTab(v)} sx={{ mb: 2 }}>
        {TOP_TABS.map((t) => (
          <Tab key={t.value} value={t.value} label={t.label} />
        ))}
      </Tabs>

      {tab === "rates" && <RatesTab />}
      {tab === "revenue" && <StoreRevenueTab />}
      {tab === "subscriptions" && <StoreSubscriptionsTab />}
      {tab === "deliveryRevenue" && <DeliveryRevenueTab />}
    </Box>
  );
}

function RatesTab() {
  const [current, setCurrent] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  function load() {
    setLoading(true);
    Promise.all([getFeeSettings(), getFeeSettingsHistory()])
      .then(([currentData, historyData]) => {
        setCurrent(currentData);
        setHistory(historyData);
        setError("");
      })
      .catch(() => setError("Could not load fee settings. Is the API running?"))
      .finally(() => setLoading(false));
  }

  useEffect(load, []);

  function startEditing() {
    setForm({
      storeCommissionPercent: current.storeCommissionPercent,
      wholesaleCommissionPercent: current.wholesaleCommissionPercent,
      deliveryCommissionPercent: current.deliveryCommissionPercent,
      subscriptionMonthlyPrice: current.subscriptionMonthlyPrice,
      trialMonths: current.trialMonths,
    });
    setEditing(true);
  }

  async function handleSave() {
    setSaving(true);
    setError("");
    try {
      await updateFeeSettings({
        storeCommissionPercent: Number(form.storeCommissionPercent),
        wholesaleCommissionPercent: Number(form.wholesaleCommissionPercent),
        deliveryCommissionPercent: Number(form.deliveryCommissionPercent),
        subscriptionMonthlyPrice: Number(form.subscriptionMonthlyPrice),
        trialMonths: Number(form.trialMonths),
      });
      setEditing(false);
      load();
    } catch {
      setError("Could not save the new rates.");
    } finally {
      setSaving(false);
    }
  }

  const historyColumns = useMemo(
    () => [
      { field: "storeCommissionPercent", headerName: "Store %", width: 100, valueFormatter: (v) => `${v}%` },
      { field: "wholesaleCommissionPercent", headerName: "Wholesale %", width: 120, valueFormatter: (v) => `${v}%` },
      { field: "deliveryCommissionPercent", headerName: "Delivery %", width: 110, valueFormatter: (v) => `${v}%` },
      {
        field: "subscriptionMonthlyPrice",
        headerName: "Subscription",
        width: 130,
        valueFormatter: (v) => formatCurrency(v),
      },
      { field: "trialMonths", headerName: "Trial (months)", width: 130 },
      { field: "effectiveFrom", headerName: "Effective From", width: 180, valueFormatter: (v) => formatDate(v) },
      {
        field: "effectiveTo",
        headerName: "Effective To",
        width: 180,
        valueFormatter: (v) => (v ? formatDate(v) : "Current"),
      },
      { field: "createdByName", headerName: "Changed By", width: 160, valueGetter: (v) => v || "-" },
    ],
    []
  );

  if (loading) return null;

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Card sx={{ maxWidth: 520, mb: 3 }}>
        <CardContent>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Current Rates
          </Typography>

          {!editing ? (
            <Stack spacing={1.5}>
              <DetailRow label="Store commission" value={`${current.storeCommissionPercent}%`} />
              <DetailRow label="Wholesale commission" value={`${current.wholesaleCommissionPercent}%`} />
              <DetailRow label="Delivery commission" value={`${current.deliveryCommissionPercent}%`} />
              <DetailRow label="Subscription price" value={formatCurrency(current.subscriptionMonthlyPrice)} />
              <DetailRow label="Free trial" value={`${current.trialMonths} months`} />
              <Divider sx={{ my: 1 }} />
              <Button variant="contained" onClick={startEditing} sx={{ alignSelf: "flex-start" }}>
                Edit Rates
              </Button>
            </Stack>
          ) : (
            <Stack spacing={2}>
              <TextField
                label="Store commission %"
                type="number"
                value={form.storeCommissionPercent}
                onChange={(e) => setForm({ ...form, storeCommissionPercent: e.target.value })}
                size="small"
              />
              <TextField
                label="Wholesale commission %"
                type="number"
                value={form.wholesaleCommissionPercent}
                onChange={(e) => setForm({ ...form, wholesaleCommissionPercent: e.target.value })}
                helperText="Taken instead of the store commission on items sold at wholesale price"
                size="small"
              />
              <TextField
                label="Delivery commission %"
                type="number"
                value={form.deliveryCommissionPercent}
                onChange={(e) => setForm({ ...form, deliveryCommissionPercent: e.target.value })}
                size="small"
              />
              <TextField
                label="Subscription price (₪/month)"
                type="number"
                value={form.subscriptionMonthlyPrice}
                onChange={(e) => setForm({ ...form, subscriptionMonthlyPrice: e.target.value })}
                size="small"
              />
              <TextField
                label="Free trial (months)"
                type="number"
                value={form.trialMonths}
                onChange={(e) => setForm({ ...form, trialMonths: e.target.value })}
                size="small"
              />
              <Stack direction="row" spacing={1}>
                <Button variant="contained" disabled={saving} onClick={handleSave}>
                  Save
                </Button>
                <Button variant="outlined" disabled={saving} onClick={() => setEditing(false)}>
                  Cancel
                </Button>
              </Stack>
            </Stack>
          )}
        </CardContent>
      </Card>

      <Typography variant="h6" sx={{ mb: 1 }}>
        Rate History
      </Typography>
      <Box sx={{ bgcolor: "background.paper", borderRadius: 2 }}>
        <DataGrid
          autoHeight
          rows={history}
          getRowId={(row) => row.feeSettingsId}
          columns={historyColumns}
          hideFooter
          disableRowSelectionOnClick
          sx={{ border: "none" }}
        />
      </Box>
    </Box>
  );
}

const REVENUE_VIEW_TABS = [
  { value: "pending", label: "Pending Fees" },
  { value: "awaiting", label: "Awaiting Payment" },
  { value: "history", label: "Collection History" },
];

function StoreRevenueTab() {
  const [view, setView] = useState("pending");
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busyStoreId, setBusyStoreId] = useState(null);
  const [selectionModel, setSelectionModel] = useState({ type: "include", ids: new Set() });
  const [bulkBusy, setBulkBusy] = useState(false);
  const selectedStoreIds = useMemo(
    () =>
      selectionModel.type === "exclude"
        ? rows.filter((r) => r.pendingAmount > 0 && !selectionModel.ids.has(r.storeId)).map((r) => r.storeId)
        : Array.from(selectionModel.ids),
    [selectionModel, rows]
  );

  const [awaitingRows, setAwaitingRows] = useState([]);
  const [awaitingRowCount, setAwaitingRowCount] = useState(0);
  const [awaitingTotalAmount, setAwaitingTotalAmount] = useState(0);
  const [awaitingLoading, setAwaitingLoading] = useState(true);
  const [awaitingPagination, setAwaitingPagination] = useState({ page: 0, pageSize: 25 });
  const [companyOptions, setCompanyOptions] = useState([]);
  const [companyFilter, setCompanyFilter] = useState("");
  const [sentStatusFilter, setSentStatusFilter] = useState("");
  const [markAllBusy, setMarkAllBusy] = useState(false);

  const [historyRows, setHistoryRows] = useState([]);
  const [historyRowCount, setHistoryRowCount] = useState(0);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [historyPagination, setHistoryPagination] = useState({ page: 0, pageSize: 25 });
  const [busySettlementId, setBusySettlementId] = useState(null);

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailTitle, setDetailTitle] = useState("");
  const [detailSummary, setDetailSummary] = useState(null);
  const [detailLines, setDetailLines] = useState([]);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState("");

  async function openPendingDetails(row) {
    setDetailOpen(true);
    setDetailTitle(row.storeName);
    setDetailSummary(null);
    setDetailLines([]);
    setDetailError("");
    setDetailLoading(true);
    try {
      const lines = await getStorePendingFees(row.storeId);
      setDetailLines(lines);
    } catch {
      setDetailError("Could not load the pending fee breakdown for this store.");
    } finally {
      setDetailLoading(false);
    }
  }

  async function openSettlementDetails(row) {
    setDetailOpen(true);
    setDetailTitle(row.storeName || row.deliveryCompanyName || "Request");
    setDetailSummary(null);
    setDetailLines([]);
    setDetailError("");
    setDetailLoading(true);
    try {
      const detail = await getSettlementDetail(row.commissionSettlementId);
      setDetailSummary(detail);
      setDetailLines(detail.lines);
    } catch {
      setDetailError("Could not load the details for this request.");
    } finally {
      setDetailLoading(false);
    }
  }

  function closeDetails() {
    setDetailOpen(false);
  }

  function loadRows() {
    setLoading(true);
    getStoreRevenue({ search: search || undefined })
      .then((data) => {
        setRows(data);
        setError("");
      })
      .catch(() => setError("Could not load store revenue. Is the API running?"))
      .finally(() => setLoading(false));
  }

  function loadAwaiting() {
    setAwaitingLoading(true);
    getSettlements({
      status: "1",
      deliveryCompanyUserId: companyFilter || undefined,
      sentStatus: sentStatusFilter || undefined,
      page: awaitingPagination.page + 1,
      pageSize: awaitingPagination.pageSize,
    })
      .then((data) => {
        setAwaitingRows(data.items);
        setAwaitingRowCount(data.totalCount);
        setAwaitingTotalAmount(data.totalAmountSum || 0);
        setError("");
      })
      .catch(() => setError("Could not load payments awaiting collection. Is the API running?"))
      .finally(() => setAwaitingLoading(false));
  }

  function loadCompanyOptions() {
    getDeliveryRevenue({})
      .then((data) =>
        setCompanyOptions(data.map((c) => ({ id: c.deliveryCompanyUserId, name: c.companyName })))
      )
      .catch(() => {});
  }

  function loadHistory() {
    setHistoryLoading(true);
    getStoreRevenueHistory({
      page: historyPagination.page + 1,
      pageSize: historyPagination.pageSize,
    })
      .then((data) => {
        setHistoryRows(data.items);
        setHistoryRowCount(data.totalCount);
        setError("");
      })
      .catch(() => setError("Could not load collection history. Is the API running?"))
      .finally(() => setHistoryLoading(false));
  }

  useEffect(loadRows, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(loadAwaiting, [awaitingPagination, companyFilter, sentStatusFilter]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(loadHistory, [historyPagination]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(loadCompanyOptions, []);

  function handleCompanyFilterChange(value) {
    setCompanyFilter(value);
    setAwaitingPagination((p) => ({ ...p, page: 0 }));
  }

  function handleSentStatusFilterChange(value) {
    setSentStatusFilter(value);
    setAwaitingPagination((p) => ({ ...p, page: 0 }));
  }

  function handleSearchSubmit(e) {
    e.preventDefault();
    loadRows();
  }

  async function handleCollect(row) {
    setBusyStoreId(row.storeId);
    try {
      await collectStoreFees(row.storeId);
      loadRows();
      loadAwaiting();
      loadHistory();
    } catch {
      setError("Could not request fees for this store.");
    } finally {
      setBusyStoreId(null);
    }
  }

  async function handleBulkCollect() {
    if (selectedStoreIds.length === 0) return;
    setBulkBusy(true);
    setError("");
    try {
      const results = await collectStoreFeesBulk(selectedStoreIds);
      const failed = results.filter((r) => !r.success);
      if (failed.length > 0) {
        setError(`Requested ${results.length - failed.length} of ${results.length} selected stores. ${failed.length} had nothing pending or failed.`);
      }
      setSelectionModel({ type: "include", ids: new Set() });
      loadRows();
      loadAwaiting();
      loadHistory();
    } catch {
      setError("Could not collect fees for the selected stores.");
    } finally {
      setBulkBusy(false);
    }
  }

  async function handleMarkReceived(row) {
    setBusySettlementId(row.commissionSettlementId);
    try {
      await markSettlementPaid(row.commissionSettlementId);
      loadAwaiting();
      loadHistory();
    } catch {
      setError("Could not mark this payment as received.");
    } finally {
      setBusySettlementId(null);
    }
  }

  async function handleMarkAllDone() {
    if (!companyFilter) return;
    setMarkAllBusy(true);
    setError("");
    try {
      const all = await getSettlements({
        status: "1",
        deliveryCompanyUserId: companyFilter,
        sentStatus: "sent",
        page: 1,
        pageSize: 1000,
      });
      await Promise.all(all.items.map((s) => markSettlementPaid(s.commissionSettlementId)));
      loadAwaiting();
      loadHistory();
    } catch {
      setError("Could not mark all payments as received for this company.");
    } finally {
      setMarkAllBusy(false);
    }
  }

  async function handleUndo(row) {
    setBusySettlementId(row.commissionSettlementId);
    try {
      if (row.partyType === 2) {
        await undoDeliveryFeesRequest(row.commissionSettlementId);
      } else {
        await undoStoreFeesCollection(row.commissionSettlementId);
      }
      loadAwaiting();
      loadHistory();
      loadRows();
    } catch {
      setError("Could not undo this collection.");
    } finally {
      setBusySettlementId(null);
    }
  }

  const columns = useMemo(
    () => [
      { field: "storeName", headerName: "Store", flex: 1, minWidth: 200 },
      {
        field: "isPrime",
        headerName: "Prime",
        width: 100,
        renderCell: (params) =>
          params.id === TOTALS_ROW_ID ? null : (
            <Chip
              size="small"
              label={params.value ? "Prime" : "Standard"}
              color={params.value ? "warning" : "default"}
            />
          ),
      },
      {
        field: "pendingProductRevenue",
        headerName: "Store Revenue (Accepted/Delivered)",
        description:
          "Prime stores count once the order is delivered. Standard stores count as soon as the order is accepted.",
        width: 220,
        valueFormatter: (v) => formatCurrency(v),
      },
      {
        field: "pendingAmount",
        headerName: "Product Fee",
        width: 150,
        valueFormatter: (v) => formatCurrency(v),
      },
      {
        field: "pendingDiscountAmount",
        headerName: "Coupon Discount (Total)",
        description:
          "Total coupon cost across these pending orders. Store-funded coupons come out of the store's own payout; platform-funded coupons (see next column) come out of our commission instead.",
        width: 200,
        valueFormatter: (v) => (v > 0 ? formatCurrency(v) : "—"),
      },
      {
        field: "pendingPlatformFundedAmount",
        headerName: "Platform-Funded Coupon Cost",
        description:
          "The slice of the coupon discount we funded ourselves (platform-funded coupons) - already subtracted from Product Fee above, not from the store.",
        width: 210,
        valueFormatter: (v) => (v > 0 ? formatCurrency(v) : "—"),
      },
      {
        field: "pendingDeliveryRevenue",
        headerName: "Delivery Revenue (Delivered Orders)",
        width: 220,
        valueFormatter: (v) => formatCurrency(v),
      },
      {
        field: "pendingDeliveryAmount",
        headerName: "Delivery Fee",
        width: 150,
        valueFormatter: (v) => formatCurrency(v),
      },
      {
        field: "details",
        headerName: "",
        width: 100,
        sortable: false,
        renderCell: (params) =>
          params.id === TOTALS_ROW_ID ? null : (
            <Button size="small" variant="outlined" onClick={() => openPendingDetails(params.row)}>
              Details
            </Button>
          ),
      },
      {
        field: "collect",
        headerName: "",
        width: 120,
        sortable: false,
        renderCell: (params) =>
          params.id === TOTALS_ROW_ID ? null : (
            <Button
              size="small"
              variant="contained"
              disabled={busyStoreId === params.row.storeId || params.row.pendingAmount === 0}
              onClick={() => handleCollect(params.row)}
            >
              Request
            </Button>
          ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [busyStoreId]
  );

  const awaitingColumns = useMemo(
    () => [
      {
        field: "partyType",
        headerName: "Source",
        width: 150,
        renderCell: (params) => (
          <Chip
            size="small"
            label={params.value === 2 ? "Delivery Commission" : "Store"}
            color={params.value === 2 ? "secondary" : "primary"}
          />
        ),
      },
      {
        field: "storeName",
        headerName: "Store",
        flex: 1,
        minWidth: 200,
        valueGetter: (v, row) => row.storeName || "-",
      },
      {
        field: "deliveryCompanyName",
        headerName: "Delivery Company",
        width: 160,
        valueGetter: (v, row) => row.deliveryCompanyName || "Store directly",
      },
      { field: "periodStart", headerName: "From", width: 150, valueFormatter: (v) => formatDate(v) },
      { field: "periodEnd", headerName: "To", width: 150, valueFormatter: (v) => formatDate(v) },
      { field: "orderCount", headerName: "Orders", width: 100 },
      { field: "totalAmount", headerName: "Amount", width: 140, valueFormatter: (v) => formatCurrency(v) },
      { field: "generatedAt", headerName: "Requested At", width: 170, valueFormatter: (v) => formatDate(v) },
      {
        field: "companyConfirmedSentAt",
        headerName: "Sent",
        width: 150,
        renderCell: (params) => {
          // Company-attached rows are confirmed by that delivery company; company-less rows (Standard
          // store, or a Prime order no company ever claimed) are confirmed by the store owner instead.
          const sentAt = params.row.deliveryCompanyName
            ? params.row.companyConfirmedSentAt
            : params.row.storeConfirmedSentAt;
          return sentAt ? (
            <Chip size="small" color="success" label={`Sent ${formatDate(sentAt)}`} />
          ) : (
            <Chip size="small" variant="outlined" label="Not yet" />
          );
        },
      },
      {
        field: "details",
        headerName: "",
        width: 100,
        sortable: false,
        renderCell: (params) => (
          <Button size="small" variant="outlined" onClick={() => openSettlementDetails(params.row)}>
            Details
          </Button>
        ),
      },
      {
        field: "done",
        headerName: "",
        width: 100,
        sortable: false,
        renderCell: (params) => (
          <Button
            size="small"
            variant="contained"
            color="success"
            disabled={busySettlementId === params.row.commissionSettlementId}
            onClick={() => handleMarkReceived(params.row)}
          >
            Done
          </Button>
        ),
      },
      {
        field: "undo",
        headerName: "",
        width: 90,
        sortable: false,
        renderCell: (params) => (
          <Button
            size="small"
            color="error"
            disabled={busySettlementId === params.row.commissionSettlementId}
            onClick={() => handleUndo(params.row)}
          >
            Undo
          </Button>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [busySettlementId]
  );

  const historyColumns = useMemo(
    () => [
      {
        field: "partyType",
        headerName: "Source",
        width: 150,
        renderCell: (params) => (
          <Chip
            size="small"
            label={params.value === 2 ? "Delivery Commission" : "Store"}
            color={params.value === 2 ? "secondary" : "primary"}
          />
        ),
      },
      { field: "storeName", headerName: "Store", flex: 1, minWidth: 200, valueGetter: (v, row) => row.storeName || "-" },
      {
        field: "deliveryCompanyName",
        headerName: "Delivery Company",
        width: 160,
        valueGetter: (v, row) => row.deliveryCompanyName || "Store directly",
      },
      { field: "periodStart", headerName: "From", width: 150, valueFormatter: (v) => formatDate(v) },
      { field: "periodEnd", headerName: "To", width: 150, valueFormatter: (v) => formatDate(v) },
      { field: "orderCount", headerName: "Orders", width: 100 },
      {
        field: "productFeeAmount",
        headerName: "Product Fee",
        width: 150,
        valueFormatter: (v) => formatCurrency(v),
      },
      {
        field: "deliveryFeeAmount",
        headerName: "Delivery Fee",
        width: 160,
        valueFormatter: (v) => formatCurrency(v),
      },
      { field: "totalAmount", headerName: "Total Collected", width: 150, valueFormatter: (v) => formatCurrency(v) },
      { field: "paidAt", headerName: "Collected At", width: 170, valueFormatter: (v) => formatDate(v) },
      {
        field: "details",
        headerName: "",
        width: 100,
        sortable: false,
        renderCell: (params) => (
          <Button size="small" variant="outlined" onClick={() => openSettlementDetails(params.row)}>
            Details
          </Button>
        ),
      },
      {
        field: "undo",
        headerName: "",
        width: 100,
        sortable: false,
        renderCell: (params) => (
          <Button
            size="small"
            color="error"
            disabled={busySettlementId === params.row.commissionSettlementId}
            onClick={() => handleUndo(params.row)}
          >
            Undo
          </Button>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [busySettlementId]
  );

  const pendingTotals = useMemo(
    () => ({
      pendingProductRevenue: rows.reduce((sum, r) => sum + r.pendingProductRevenue, 0),
      pendingAmount: rows.reduce((sum, r) => sum + r.pendingAmount, 0),
      pendingDiscountAmount: rows.reduce((sum, r) => sum + r.pendingDiscountAmount, 0),
      pendingPlatformFundedAmount: rows.reduce((sum, r) => sum + r.pendingPlatformFundedAmount, 0),
      pendingDeliveryRevenue: rows.reduce((sum, r) => sum + r.pendingDeliveryRevenue, 0),
      pendingDeliveryAmount: rows.reduce((sum, r) => sum + r.pendingDeliveryAmount, 0),
    }),
    [rows]
  );

  // A synthetic last row rather than a separate stat bar, so each total lines up directly under
  // its own column instead of being clumped off to one side.
  const pendingRowsWithTotal = useMemo(() => {
    if (rows.length === 0) return rows;
    return [
      ...rows,
      {
        storeId: TOTALS_ROW_ID,
        storeName: "Total",
        isPrime: null,
        ...pendingTotals,
      },
    ];
  }, [rows, pendingTotals]);

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Tabs value={view} onChange={(e, v) => setView(v)} sx={{ mb: 2 }}>
        {REVENUE_VIEW_TABS.map((t) => (
          <Tab key={t.value} value={t.value} label={t.label} />
        ))}
      </Tabs>

      {view === "pending" && (
        <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Fees owed right now, from confirmed orders that haven't been collected yet - includes both
            the product fee and the delivery fee for this store's orders, so there's no need to also
            visit Delivery Companies to collect the delivery side. An order counts here once it's
            confirmed: for Prime stores that's once the order is delivered (they're served by delivery
            companies), for Standard stores that's as soon as the store accepts the order (they handle
            their own delivery). Clicking Request never collects money on the spot - it moves the
            amount to Awaiting Payment first. For Prime stores that's split per delivery company that
            handled the orders, and that company confirms sending it. For Standard stores (or any
            order no delivery company ever claimed) it's owed by the store directly, and the store
            owner confirms sending it themselves from their app. Either way, you click Done once the
            money actually arrives, which moves it to Collection History. Select multiple stores with
            the checkboxes to request all of them in one go.
          </Typography>

          <Stack component="form" onSubmit={handleSearchSubmit} direction="row" spacing={2} sx={{ mb: 2 }}>
            <TextField
              label="Search store"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              size="small"
              sx={{ minWidth: 240 }}
            />
            <Button
              variant="contained"
              disabled={selectedStoreIds.length === 0 || bulkBusy}
              onClick={handleBulkCollect}
            >
              {bulkBusy ? "Processing..." : `Request Selected (${selectedStoreIds.length})`}
            </Button>
          </Stack>

          <Box sx={{ bgcolor: "background.paper", borderRadius: 2 }}>
            <DataGrid
              autoHeight
              rows={pendingRowsWithTotal}
              getRowId={(row) => row.storeId}
              columns={columns}
              loading={loading}
              hideFooter
              checkboxSelection
              isRowSelectable={(params) => params.id !== TOTALS_ROW_ID && params.row.pendingAmount > 0}
              rowSelectionModel={selectionModel}
              onRowSelectionModelChange={setSelectionModel}
              getRowClassName={(params) => (params.id === TOTALS_ROW_ID ? "totals-row" : "")}
              sx={{
                border: "none",
                "& .totals-row": {
                  bgcolor: "action.hover",
                  fontWeight: 700,
                  "& .MuiDataGrid-cell": { fontWeight: 700, borderTop: 2, borderTopColor: "divider" },
                },
              }}
            />
          </Box>
        </>
      )}

      {view === "awaiting" && (
        <Box sx={{ bgcolor: "background.paper", borderRadius: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ px: 2, pt: 2 }}>
            Includes both store fees requested through a delivery company and delivery companies' own
            commission requests - tagged by Source below - so everything currently owed to you shows in
            one place.
          </Typography>
          <Stack direction="row" spacing={2} alignItems="center" sx={{ p: 2 }}>
            <FormControl size="small" sx={{ minWidth: 240 }}>
              <InputLabel id="awaiting-company-filter-label">Filter by company</InputLabel>
              <Select
                labelId="awaiting-company-filter-label"
                label="Filter by company"
                value={companyFilter}
                onChange={(e) => handleCompanyFilterChange(e.target.value)}
              >
                <MenuItem value="">All companies</MenuItem>
                {companyOptions.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel id="awaiting-sent-status-filter-label">Sent status</InputLabel>
              <Select
                labelId="awaiting-sent-status-filter-label"
                label="Sent status"
                value={sentStatusFilter}
                onChange={(e) => handleSentStatusFilterChange(e.target.value)}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="sent">Sent</MenuItem>
                <MenuItem value="notYet">Not yet</MenuItem>
                <MenuItem value="without">Store-direct only (no delivery company)</MenuItem>
              </Select>
            </FormControl>
            <Button
              variant="contained"
              color="success"
              disabled={!companyFilter || markAllBusy}
              onClick={handleMarkAllDone}
            >
              {markAllBusy ? "Processing..." : "Mark All Done"}
            </Button>
          </Stack>
          <DataGrid
            autoHeight
            rows={awaitingRows}
            getRowId={(row) => row.commissionSettlementId}
            columns={awaitingColumns}
            rowCount={awaitingRowCount}
            loading={awaitingLoading}
            paginationMode="server"
            paginationModel={awaitingPagination}
            onPaginationModelChange={setAwaitingPagination}
            pageSizeOptions={[10, 25, 50, 100]}
            disableRowSelectionOnClick
            sx={{ border: "none" }}
          />
          <Stack
            direction="row"
            justifyContent="flex-end"
            sx={{ px: 2, py: 1.5, borderTop: 1, borderColor: "divider" }}
          >
            <TotalStat label="Total Awaiting Payment" value={formatCurrency(awaitingTotalAmount)} />
          </Stack>
        </Box>
      )}

      {view === "history" && (
        <Box sx={{ bgcolor: "background.paper", borderRadius: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ px: 2, pt: 2 }}>
            Every batch you've actually collected - store fees and delivery companies' own commission
            together, tagged by Source below.
          </Typography>
          <DataGrid
            autoHeight
            rows={historyRows}
            getRowId={(row) => row.commissionSettlementId}
            columns={historyColumns}
            rowCount={historyRowCount}
            loading={historyLoading}
            paginationMode="server"
            paginationModel={historyPagination}
            onPaginationModelChange={setHistoryPagination}
            pageSizeOptions={[10, 25, 50, 100]}
            disableRowSelectionOnClick
            sx={{ border: "none" }}
          />
        </Box>
      )}

      <Dialog open={detailOpen} onClose={closeDetails} maxWidth="md" fullWidth>
        <DialogTitle>{detailTitle}</DialogTitle>
        <DialogContent dividers>
          {detailError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {detailError}
            </Alert>
          )}

          {detailLoading ? (
            <Stack alignItems="center" sx={{ py: 4 }}>
              <CircularProgress size={28} />
            </Stack>
          ) : (
            <>
              {detailSummary && (
                <Stack direction="row" spacing={4} sx={{ mb: 2 }} flexWrap="wrap">
                  <DetailRow label="Delivery company" value={detailSummary.deliveryCompanyName || "Store directly"} />
                  <DetailRow label="Period" value={`${formatDate(detailSummary.periodStart)} - ${formatDate(detailSummary.periodEnd)}`} />
                  <DetailRow label="Total" value={formatCurrency(detailSummary.totalAmount)} />
                  <DetailRow label="Status" value={detailSummary.status === 2 ? "Paid" : "Generated"} />
                </Stack>
              )}

              {detailLines.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No order-level data to show.
                </Typography>
              ) : (
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Order</TableCell>
                      <TableCell>Fee Type</TableCell>
                      <TableCell>Delivery Company</TableCell>
                      <TableCell align="right">Base Amount</TableCell>
                      <TableCell align="right">%</TableCell>
                      <TableCell align="right">Fee Amount</TableCell>
                      <TableCell>Coupon</TableCell>
                      <TableCell align="right">Coupon Discount</TableCell>
                      <TableCell>Confirmed At</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {detailLines.map((line, i) => (
                      <TableRow key={`${line.orderId}-${line.feeType}-${i}`}>
                        <TableCell>{line.orderNumber}</TableCell>
                        <TableCell>
                          <Chip
                            size="small"
                            label={line.feeType === "Delivery" ? "Delivery" : line.feeType === "Wholesale" ? "Wholesale" : "Product"}
                            color={line.feeType === "Delivery" ? "secondary" : line.feeType === "Wholesale" ? "warning" : "primary"}
                          />
                        </TableCell>
                        <TableCell>{line.deliveryCompanyName || "-"}</TableCell>
                        <TableCell align="right">{formatCurrency(line.baseAmount)}</TableCell>
                        <TableCell align="right">{line.percent}%</TableCell>
                        <TableCell align="right">{formatCurrency(line.amount)}</TableCell>
                        <TableCell>
                          {line.couponCode ? (
                            <Chip
                              size="small"
                              label={couponLabel(line.couponCode, line.couponDiscountType)}
                              color={[3, 4].includes(line.couponDiscountType) ? "info" : "default"}
                            />
                          ) : (
                            "-"
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {line.discountAmount > 0 ? (
                            <Stack direction="column" alignItems="flex-end">
                              <span>{formatCurrency(line.discountAmount)}</span>
                              {line.platformFundedDiscountAmount > 0 && (
                                <Chip
                                  size="small"
                                  label={`We paid ${formatCurrency(line.platformFundedDiscountAmount)}`}
                                  color="primary"
                                  variant="outlined"
                                />
                              )}
                            </Stack>
                          ) : (
                            "-"
                          )}
                        </TableCell>
                        <TableCell>{formatDate(line.confirmedAt)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDetails}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

const SUBSCRIPTION_STATUS_OPTIONS = [
  { value: "", label: "All statuses" },
  { value: "1", label: "Trial" },
  { value: "2", label: "Active" },
  { value: "3", label: "Past Due" },
  { value: "4", label: "Suspended" },
  { value: "5", label: "Cancelled" },
];

function isTrialActive(row) {
  return row.status === 1 && row.trialEndsAt && new Date(row.trialEndsAt) > new Date();
}

// Mirrors the backend's "hasActivePeriod" check in RenewSubscription: Active/PastDue with time still
// left on the current period means a renewal should queue after it instead of starting immediately.
function renewHasActivePeriod(row) {
  return (row.status === 2 || row.status === 3) && row.currentPeriodEnd && new Date(row.currentPeriodEnd) > new Date();
}

function StoreSubscriptionsTab() {
  const [rows, setRows] = useState([]);
  const [rowCount, setRowCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [pagination, setPagination] = useState({ page: 0, pageSize: 25 });

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailStoreId, setDetailStoreId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState("");
  const [busy, setBusy] = useState(false);
  const [overrideStatus, setOverrideStatus] = useState("");
  const [overrideNote, setOverrideNote] = useState("");
  const [busyInvoiceId, setBusyInvoiceId] = useState(null);
  const [priceValue, setPriceValue] = useState("");
  const [priceBusy, setPriceBusy] = useState(false);

  const [renewOpen, setRenewOpen] = useState(false);
  const [renewRow, setRenewRow] = useState(null);
  const [renewMode, setRenewMode] = useState("duration");
  const [renewMonths, setRenewMonths] = useState(1);
  const [renewFrom, setRenewFrom] = useState("");
  const [renewTo, setRenewTo] = useState("");
  const [renewAmount, setRenewAmount] = useState("");
  const [renewMarkPaid, setRenewMarkPaid] = useState(false);
  const [renewNote, setRenewNote] = useState("");
  const [renewBusy, setRenewBusy] = useState(false);
  const [renewError, setRenewError] = useState("");

  function loadRows() {
    setLoading(true);
    getStoreSubscriptions({
      status: statusFilter || undefined,
      search: search || undefined,
      page: pagination.page + 1,
      pageSize: pagination.pageSize,
    })
      .then((data) => {
        setRows(data.items);
        setRowCount(data.totalCount);
        setError("");
      })
      .catch(() => setError("Could not load store subscriptions. Is the API running?"))
      .finally(() => setLoading(false));
  }

  useEffect(loadRows, [statusFilter, pagination]); // eslint-disable-line react-hooks/exhaustive-deps

  function handleSearchSubmit(e) {
    e.preventDefault();
    setPagination((p) => ({ ...p, page: 0 }));
    loadRows();
  }

  function loadDetail(storeId) {
    setDetailLoading(true);
    setDetailError("");
    getStoreSubscriptionDetail(storeId)
      .then((data) => {
        setDetail(data);
        setOverrideStatus(String(data.status));
        setOverrideNote("");
        setPriceValue(data.isPriceOverridden ? String(data.effectiveMonthlyPrice) : "");
      })
      .catch(() => setDetailError("Could not load the subscription detail for this store."))
      .finally(() => setDetailLoading(false));
  }

  function openDetail(row) {
    setDetailStoreId(row.storeId);
    setDetail(null);
    setDetailOpen(true);
    loadDetail(row.storeId);
  }

  function closeDetail() {
    setDetailOpen(false);
    setDetailStoreId(null);
    setDetail(null);
  }

  async function handleOverride() {
    if (!detailStoreId) return;
    setBusy(true);
    setDetailError("");
    try {
      await overrideStoreSubscriptionStatus(detailStoreId, Number(overrideStatus), overrideNote || undefined);
      loadDetail(detailStoreId);
      loadRows();
    } catch {
      setDetailError("Could not override the subscription status.");
    } finally {
      setBusy(false);
    }
  }

  async function handleClearOverride() {
    if (!detailStoreId) return;
    setBusy(true);
    setDetailError("");
    try {
      await clearStoreSubscriptionOverride(detailStoreId);
      loadDetail(detailStoreId);
      loadRows();
    } catch {
      setDetailError("Could not clear the override.");
    } finally {
      setBusy(false);
    }
  }

  async function handleSavePriceOverride() {
    if (!detailStoreId) return;
    setPriceBusy(true);
    setDetailError("");
    try {
      const value = priceValue === "" ? null : Number(priceValue);
      await setSubscriptionPriceOverride(detailStoreId, value);
      loadDetail(detailStoreId);
      loadRows();
    } catch {
      setDetailError("Could not update this store's subscription price.");
    } finally {
      setPriceBusy(false);
    }
  }

  async function handleMarkInvoicePaid(invoice) {
    if (!detailStoreId) return;
    setBusyInvoiceId(invoice.subscriptionInvoiceId);
    setDetailError("");
    try {
      await markInvoicePaid(detailStoreId, invoice.subscriptionInvoiceId);
      loadDetail(detailStoreId);
      loadRows();
    } catch {
      setDetailError("Could not mark this invoice as paid.");
    } finally {
      setBusyInvoiceId(null);
    }
  }

  function openRenew(row) {
    setRenewRow(row);
    setRenewMode("duration");
    setRenewMonths(1);
    setRenewFrom("");
    setRenewTo("");
    setRenewAmount(row.effectiveMonthlyPrice != null ? String(row.effectiveMonthlyPrice) : "");
    setRenewMarkPaid(false);
    setRenewNote("");
    setRenewError("");
    setRenewOpen(true);
  }

  function closeRenew() {
    setRenewOpen(false);
    setRenewRow(null);
  }

  function handleRenewMonthsChange(months) {
    setRenewMonths(months);
    if (renewRow?.effectiveMonthlyPrice != null) {
      setRenewAmount(String(Number((renewRow.effectiveMonthlyPrice * months).toFixed(2))));
    }
  }

  const renewFormValid =
    renewRow &&
    renewAmount !== "" &&
    Number(renewAmount) >= 0 &&
    (renewMode === "duration" || (renewFrom && renewTo && renewTo > renewFrom));

  async function handleRenew() {
    if (!renewRow || !renewFormValid) return;
    setRenewBusy(true);
    setRenewError("");
    try {
      const payload = {
        amount: Number(renewAmount),
        markPaid: renewMarkPaid,
        note: renewNote || undefined,
      };
      if (renewMode === "duration") {
        payload.months = renewMonths;
      } else {
        payload.periodStart = renewFrom;
        payload.periodEnd = renewTo;
      }
      await renewStoreSubscription(renewRow.storeId, payload);
      closeRenew();
      loadRows();
      if (detailStoreId === renewRow.storeId) loadDetail(renewRow.storeId);
    } catch {
      setRenewError("Could not renew this store's subscription.");
    } finally {
      setRenewBusy(false);
    }
  }

  const columns = useMemo(
    () => [
      { field: "storeName", headerName: "Store", flex: 1, minWidth: 180 },
      { field: "ownerName", headerName: "Owner", width: 160 },
      {
        field: "status",
        headerName: "Status",
        width: 130,
        renderCell: (params) => (
          <Chip
            size="small"
            label={SubscriptionStatusLabels[params.value] || params.value}
            color={SubscriptionStatusColors[params.value] || "default"}
          />
        ),
      },
      {
        field: "trial",
        headerName: "Trial",
        width: 110,
        sortable: false,
        renderCell: (params) =>
          isTrialActive(params.row) ? (
            <Chip size="small" color="info" label="In Trial" />
          ) : (
            <Chip size="small" variant="outlined" label="No" />
          ),
      },
      { field: "trialEndsAt", headerName: "Trial Ends", width: 170, valueFormatter: (v) => formatDate(v) },
      {
        field: "currentPeriodEnd",
        headerName: "Current Period Ends",
        width: 180,
        valueFormatter: (v) => (v ? formatDate(v) : "-"),
      },
      {
        field: "graceEndsAt",
        headerName: "Grace Ends",
        width: 170,
        valueFormatter: (v) => (v ? formatDate(v) : "-"),
      },
      {
        field: "ended",
        headerName: "Subscription Ended",
        width: 150,
        sortable: false,
        renderCell: (params) =>
          params.row.status === 5 ? (
            <Chip size="small" color="error" label="Ended" />
          ) : (
            <Chip size="small" color="success" variant="outlined" label="Ongoing" />
          ),
      },
      {
        field: "effectiveCommissionPercent",
        headerName: "Commission %",
        width: 130,
        valueFormatter: (v) => `${v}%`,
      },
      {
        field: "effectiveMonthlyPrice",
        headerName: "Price / mo",
        width: 150,
        sortable: false,
        renderCell: (params) => (
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Typography variant="body2">{formatCurrency(params.value)}</Typography>
            {params.row.isPriceOverridden && <Chip size="small" variant="outlined" color="info" label="Custom" />}
          </Stack>
        ),
      },
      {
        field: "isOverridden",
        headerName: "Overridden",
        width: 110,
        renderCell: (params) =>
          params.value ? <Chip size="small" color="warning" label="Yes" /> : <Chip size="small" variant="outlined" label="No" />,
      },
      {
        field: "details",
        headerName: "",
        width: 100,
        sortable: false,
        renderCell: (params) => (
          <Button size="small" variant="outlined" onClick={() => openDetail(params.row)}>
            Details
          </Button>
        ),
      },
      {
        field: "renew",
        headerName: "",
        width: 100,
        sortable: false,
        renderCell: (params) => (
          <Button size="small" variant="contained" color="success" onClick={() => openRenew(params.row)}>
            Renew
          </Button>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const invoiceColumns = useMemo(
    () => [
      { field: "periodStart", headerName: "Period Start", flex: 1, minWidth: 150, valueFormatter: (v) => formatDate(v) },
      { field: "periodEnd", headerName: "Period End", flex: 1, minWidth: 150, valueFormatter: (v) => formatDate(v) },
      { field: "amount", headerName: "Amount", width: 120, valueFormatter: (v) => formatCurrency(v) },
      { field: "dueDate", headerName: "Due Date", width: 150, valueFormatter: (v) => formatDate(v) },
      {
        field: "status",
        headerName: "Status",
        width: 120,
        renderCell: (params) => (
          <Chip
            size="small"
            label={InvoiceStatusLabels[params.value] || params.value}
            color={InvoiceStatusColors[params.value] || "default"}
          />
        ),
      },
      { field: "paidAt", headerName: "Paid At", width: 170, valueFormatter: (v) => (v ? formatDate(v) : "-") },
      { field: "note", headerName: "Note", flex: 1, minWidth: 140, valueGetter: (v) => v || "-" },
      {
        field: "action",
        headerName: "",
        width: 70,
        sortable: false,
        renderCell: (params) =>
          params.row.status === 1 || params.row.status === 3 ? (
            <Tooltip title="Mark as paid">
              <span>
                <IconButton
                  size="small"
                  color="success"
                  disabled={busyInvoiceId === params.row.subscriptionInvoiceId}
                  onClick={() => handleMarkInvoicePaid(params.row)}
                >
                  <CheckCircleIcon />
                </IconButton>
              </span>
            </Tooltip>
          ) : null,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [busyInvoiceId]
  );

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Every store's subscription: whether it's still on the free trial, when the trial or current
        billing period ends, whether it's fallen into a grace period, and whether it has ended
        (Cancelled) or is still ongoing. Click Details for the full billing history and to override a
        status or mark an invoice paid by hand.
      </Typography>

      <Stack component="form" onSubmit={handleSearchSubmit} direction="row" spacing={2} sx={{ mb: 2 }}>
        <TextField
          label="Search store or owner"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          size="small"
          sx={{ minWidth: 240 }}
        />
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel id="subscription-status-filter-label">Status</InputLabel>
          <Select
            labelId="subscription-status-filter-label"
            label="Status"
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPagination((p) => ({ ...p, page: 0 }));
            }}
          >
            {SUBSCRIPTION_STATUS_OPTIONS.map((o) => (
              <MenuItem key={o.value} value={o.value}>
                {o.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button type="submit" variant="contained">
          Search
        </Button>
      </Stack>

      <Box sx={{ bgcolor: "background.paper", borderRadius: 2 }}>
        <DataGrid
          autoHeight
          rows={rows}
          getRowId={(row) => row.storeSubscriptionId}
          columns={columns}
          rowCount={rowCount}
          loading={loading}
          paginationMode="server"
          paginationModel={pagination}
          onPaginationModelChange={setPagination}
          pageSizeOptions={[10, 25, 50, 100]}
          disableRowSelectionOnClick
          sx={{ border: "none" }}
        />
      </Box>

      <Dialog open={detailOpen} onClose={closeDetail} maxWidth="md" fullWidth>
        <DialogTitle>{detail?.storeName || "Subscription Detail"}</DialogTitle>
        <DialogContent dividers>
          {detailError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {detailError}
            </Alert>
          )}

          {detailLoading || !detail ? (
            <Stack alignItems="center" sx={{ py: 4 }}>
              <CircularProgress size={28} />
            </Stack>
          ) : (
            <>
              <Stack direction="row" spacing={4} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
                <DetailRow label="Owner" value={detail.ownerName} />
                <DetailRow
                  label="Status"
                  value={SubscriptionStatusLabels[detail.status] || detail.status}
                />
                <DetailRow label="In trial" value={isTrialActive(detail) ? "Yes" : "No"} />
                <DetailRow label="Trial ends" value={formatDate(detail.trialEndsAt)} />
                <DetailRow
                  label="Current period"
                  value={
                    detail.currentPeriodStart
                      ? `${formatDate(detail.currentPeriodStart)} - ${formatDate(detail.currentPeriodEnd)}`
                      : "-"
                  }
                />
                <DetailRow label="Grace ends" value={detail.graceEndsAt ? formatDate(detail.graceEndsAt) : "-"} />
                <DetailRow label="Subscription ended" value={detail.status === 5 ? "Yes" : "No"} />
                <DetailRow label="Commission %" value={`${detail.effectiveCommissionPercent}%`} />
                <DetailRow label="Overridden" value={detail.isOverridden ? "Yes" : "No"} />
                <DetailRow
                  label="Subscription price"
                  value={`${formatCurrency(detail.effectiveMonthlyPrice)} / mo${detail.isPriceOverridden ? " (custom)" : " (default)"}`}
                />
              </Stack>

              <Button
                variant="contained"
                color="success"
                sx={{ mb: 2 }}
                onClick={() =>
                  openRenew({
                    storeId: detail.storeId,
                    storeName: detail.storeName,
                    status: detail.status,
                    currentPeriodEnd: detail.currentPeriodEnd,
                    effectiveMonthlyPrice: detail.effectiveMonthlyPrice,
                  })
                }
              >
                Renew Subscription
              </Button>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Subscription Price
              </Typography>
              <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }} flexWrap="wrap">
                <TextField
                  label="Custom price / month (blank = default)"
                  type="number"
                  size="small"
                  value={priceValue}
                  onChange={(e) => setPriceValue(e.target.value)}
                  sx={{ minWidth: 260 }}
                />
                <Button variant="contained" disabled={priceBusy} onClick={handleSavePriceOverride}>
                  Save Price
                </Button>
              </Stack>

              <Divider sx={{ my: 2 }} />

              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Override Status
              </Typography>
              <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }} flexWrap="wrap">
                <FormControl size="small" sx={{ minWidth: 160 }}>
                  <InputLabel id="override-status-label">New status</InputLabel>
                  <Select
                    labelId="override-status-label"
                    label="New status"
                    value={overrideStatus}
                    onChange={(e) => setOverrideStatus(e.target.value)}
                  >
                    {SUBSCRIPTION_STATUS_OPTIONS.filter((o) => o.value).map((o) => (
                      <MenuItem key={o.value} value={o.value}>
                        {o.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  label="Note (optional)"
                  size="small"
                  value={overrideNote}
                  onChange={(e) => setOverrideNote(e.target.value)}
                  sx={{ minWidth: 220 }}
                />
                <Button variant="contained" disabled={busy} onClick={handleOverride}>
                  Override
                </Button>
                {detail.isOverridden && (
                  <Button variant="outlined" color="warning" disabled={busy} onClick={handleClearOverride}>
                    Clear Override
                  </Button>
                )}
              </Stack>

              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Invoices
              </Typography>
              {detail.invoices.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  No invoices have been generated yet.
                </Typography>
              ) : (
                <DataGrid
                  autoHeight
                  rows={detail.invoices}
                  getRowId={(row) => row.subscriptionInvoiceId}
                  columns={invoiceColumns}
                  hideFooter
                  disableRowSelectionOnClick
                  sx={{ border: "none" }}
                />
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDetail}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={renewOpen} onClose={closeRenew} maxWidth="xs" fullWidth>
        <DialogTitle>Renew Subscription{renewRow ? ` - ${renewRow.storeName}` : ""}</DialogTitle>
        <DialogContent dividers>
          {renewError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {renewError}
            </Alert>
          )}

          {renewMode === "duration" && renewRow && renewHasActivePeriod(renewRow) ? (
            <Alert severity="info" sx={{ mb: 2 }}>
              Still has a paid period running until {formatDate(renewRow.currentPeriodEnd)}. This
              queues the renewal to start right after that - the current period keeps running
              uninterrupted.
            </Alert>
          ) : (
            <Alert severity="info" sx={{ mb: 2 }}>
              {renewMode === "duration"
                ? "There's no subscription currently running, so this starts a new Active period immediately."
                : "This creates a period for exactly the dates you choose below."}
            </Alert>
          )}

          <RadioGroup
            row
            value={renewMode}
            onChange={(e) => setRenewMode(e.target.value)}
            sx={{ mb: 1 }}
          >
            <FormControlLabel value="duration" control={<Radio size="small" />} label="Duration" />
            <FormControlLabel value="custom" control={<Radio size="small" />} label="Custom dates" />
          </RadioGroup>

          {renewMode === "duration" ? (
            <FormControl size="small" fullWidth sx={{ mb: 2 }}>
              <InputLabel id="renew-months-label">Duration</InputLabel>
              <Select
                labelId="renew-months-label"
                label="Duration"
                value={renewMonths}
                onChange={(e) => handleRenewMonthsChange(Number(e.target.value))}
              >
                <MenuItem value={1}>1 month</MenuItem>
                <MenuItem value={3}>3 months</MenuItem>
                <MenuItem value={6}>6 months</MenuItem>
                <MenuItem value={12}>12 months</MenuItem>
              </Select>
            </FormControl>
          ) : (
            <Stack direction="row" spacing={2} sx={{ mb: 2 }}>
              <TextField
                label="From"
                type="date"
                size="small"
                fullWidth
                value={renewFrom}
                onChange={(e) => setRenewFrom(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
              <TextField
                label="To"
                type="date"
                size="small"
                fullWidth
                value={renewTo}
                onChange={(e) => setRenewTo(e.target.value)}
                InputLabelProps={{ shrink: true }}
              />
            </Stack>
          )}

          <TextField
            label="Price for this period"
            type="number"
            size="small"
            fullWidth
            value={renewAmount}
            onChange={(e) => setRenewAmount(e.target.value)}
            sx={{ mb: 1 }}
          />

          <FormControlLabel
            control={<Checkbox checked={renewMarkPaid} onChange={(e) => setRenewMarkPaid(e.target.checked)} />}
            label="Already paid - mark as Paid right away"
            sx={{ mb: 1, display: "block" }}
          />

          <TextField
            label="Note (optional)"
            value={renewNote}
            onChange={(e) => setRenewNote(e.target.value)}
            size="small"
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeRenew} disabled={renewBusy}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="success"
            disabled={renewBusy || !renewFormValid}
            onClick={handleRenew}
          >
            {renewBusy ? "Renewing..." : "Renew"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

function DeliveryRevenueTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busyCompanyId, setBusyCompanyId] = useState(null);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [storeBreakdown, setStoreBreakdown] = useState([]);
  const [breakdownLoading, setBreakdownLoading] = useState(false);

  function loadRows() {
    setLoading(true);
    getDeliveryRevenue({})
      .then((data) => {
        setRows(data);
        setError("");
      })
      .catch(() => setError("Could not load delivery company revenue. Is the API running?"))
      .finally(() => setLoading(false));
  }

  useEffect(loadRows, []);

  async function handleRequest(row) {
    setBusyCompanyId(row.deliveryCompanyUserId);
    try {
      await requestDeliveryFees(row.deliveryCompanyUserId);
      loadRows();
    } catch {
      setError("Could not request fees from this delivery company.");
    } finally {
      setBusyCompanyId(null);
    }
  }

  // Marking a company "Done" settles everything they owe in one go: their own delivery
  // commission (PartyType DeliveryCompany) and any Prime store product fees their drivers
  // delivered, which get requested from them too (PartyType Store, split by company - see
  // AdminFeesController.CollectStoreFeesInternal). Both surface here since a company hands
  // over one lump sum covering both.
  async function handleMarkReceived(row) {
    setBusyCompanyId(row.deliveryCompanyUserId);
    try {
      const generated = await getSettlements({
        status: "1",
        deliveryCompanyUserId: row.deliveryCompanyUserId,
        pageSize: 100,
      });
      await Promise.all(generated.items.map((s) => markSettlementPaid(s.commissionSettlementId)));
      loadRows();
      if (selectedCompany?.deliveryCompanyUserId === row.deliveryCompanyUserId)
        handleViewStores(row);
    } catch {
      setError("Could not mark this payment as received.");
    } finally {
      setBusyCompanyId(null);
    }
  }

  async function handleViewStores(row) {
    setSelectedCompany(row);
    setBreakdownLoading(true);
    try {
      const data = await getCompanyStoreBreakdown(row.deliveryCompanyUserId);
      setStoreBreakdown(data);
      setError("");
    } catch {
      setError("Could not load the store breakdown for this company.");
    } finally {
      setBreakdownLoading(false);
    }
  }

  const columns = useMemo(
    () => [
      { field: "companyName", headerName: "Delivery Company", flex: 1, minWidth: 200 },
      {
        field: "pendingAmount",
        headerName: "Pending Fee (10%)",
        width: 160,
        valueFormatter: (v) => formatCurrency(v),
      },
      {
        field: "requestedAmount",
        headerName: "Awaiting Payment",
        width: 160,
        valueFormatter: (v) => formatCurrency(v),
      },
      {
        field: "actions",
        headerName: "",
        width: 280,
        sortable: false,
        renderCell: (params) => (
          <Stack direction="row" spacing={1}>
            <Button
              size="small"
              variant="outlined"
              onClick={() => handleViewStores(params.row)}
            >
              Stores
            </Button>
            {params.row.pendingAmount > 0 && (
              <Button
                size="small"
                variant="contained"
                disabled={busyCompanyId === params.row.deliveryCompanyUserId}
                onClick={() => handleRequest(params.row)}
              >
                Request
              </Button>
            )}
            {params.row.requestedAmount > 0 && (
              <Button
                size="small"
                variant="contained"
                color="success"
                disabled={busyCompanyId === params.row.deliveryCompanyUserId}
                onClick={() => handleMarkReceived(params.row)}
              >
                Done
              </Button>
            )}
          </Stack>
        ),
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [busyCompanyId]
  );

  const storeBreakdownColumns = useMemo(
    () => [
      { field: "storeName", headerName: "Store", flex: 1, minWidth: 200 },
      { field: "pendingOrderCount", headerName: "Pending Orders", width: 140 },
      {
        field: "pendingAmount",
        headerName: "Pending Product Fee (5%)",
        width: 190,
        valueFormatter: (v) => formatCurrency(v),
      },
      { field: "requestedOrderCount", headerName: "Requested Orders", width: 150 },
      {
        field: "requestedAmount",
        headerName: "Awaiting Payment",
        width: 160,
        valueFormatter: (v) => formatCurrency(v),
      },
      {
        field: "totalCollected",
        headerName: "Total Collected",
        width: 150,
        valueFormatter: (v) => formatCurrency(v),
      },
      {
        field: "lastCollectedAt",
        headerName: "Last Collected",
        width: 160,
        valueFormatter: (v) => formatDate(v),
      },
    ],
    []
  );

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Delivery-order fees (10%) owed by each delivery company right now. Collecting/requesting fees
        from a store under Store Revenue already sweeps up that store's delivery fees too, so this tab
        is normally just a fallback for whatever wasn't picked up that way. Click Request once you've
        told them what they owe - it moves to Awaiting Payment. Once they actually hand over the money,
        click Done to confirm receipt.
      </Typography>

      <Box sx={{ bgcolor: "background.paper", borderRadius: 2 }}>
        <DataGrid
          autoHeight
          rows={rows}
          getRowId={(row) => row.deliveryCompanyUserId}
          columns={columns}
          loading={loading}
          hideFooter
          disableRowSelectionOnClick
          sx={{ border: "none" }}
        />
      </Box>

      {selectedCompany && (
        <Box sx={{ mt: 3 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="subtitle1">
              Stores serviced by {selectedCompany.companyName}
            </Typography>
            <Button size="small" onClick={() => setSelectedCompany(null)}>
              Close
            </Button>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Product-fee (5%) obligations for Prime stores whose orders this company's drivers actually
            delivered - separate from the delivery commission above.
          </Typography>
          <Box sx={{ bgcolor: "background.paper", borderRadius: 2 }}>
            <DataGrid
              autoHeight
              rows={storeBreakdown}
              getRowId={(row) => row.storeId}
              columns={storeBreakdownColumns}
              loading={breakdownLoading}
              hideFooter
              disableRowSelectionOnClick
              sx={{ border: "none" }}
            />
          </Box>
        </Box>
      )}
    </Box>
  );
}

function TotalStat({ label, value }) {
  return (
    <Stack alignItems="flex-end">
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={700}>
        {value}
      </Typography>
    </Stack>
  );
}

function DetailRow({ label, value }) {
  return (
    <Stack direction="row" justifyContent="space-between" alignItems="center">
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" fontWeight={600} sx={{ textAlign: "right", maxWidth: 220 }}>
        {value}
      </Typography>
    </Stack>
  );
}
